// CustomerPaymentStep.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { Search, User, Check, Loader2, Phone, ChevronDown, CreditCard } from "lucide-react";
import { toast } from "react-toastify";

const toEnglishDigits = (str) => {
  if (!str) return "";
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  const englishDigits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
  return str.replace(/[۰-۹]/g, (d) => englishDigits[persianDigits.indexOf(d)]);
};

const CustomerPaymentStep = ({
  axiosAdmin,
  onBack,
  onSubmit,
  phone: initialPhone,
  firstName: initialFirstName,
  lastName: initialLastName,
  paymentType: initialPaymentType,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [phone, setPhone] = useState(initialPhone || "");
  const [firstName, setFirstName] = useState(initialFirstName || "");
  const [lastName, setLastName] = useState(initialLastName || "");
  const [paymentType, setPaymentType] = useState(initialPaymentType || "کارت به کارت");

  const debounceTimer = useRef(null);
  const phoneTimer = useRef(null);
  const lookupInProgress = useRef(false);
  const phoneRef = useRef(phone);
  const selectedUserRef = useRef(selectedUser);

  useEffect(() => { phoneRef.current = phone; }, [phone]);
  useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);

  useEffect(() => {
    if (initialPhone) setPhone(initialPhone);
    if (initialFirstName) setFirstName(initialFirstName);
    if (initialLastName) setLastName(initialLastName);
    if (initialPaymentType) setPaymentType(initialPaymentType);
  }, [initialPhone, initialFirstName, initialLastName, initialPaymentType]);

  const searchUsers = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      setIsSearching(true);
      const res = await axiosAdmin.get("/api/admin/users", {
        params: { search: query, limit: 50, sort: "newest" },
      });
      setSearchResults(res.data.users || []);
      setShowDropdown(true);
    } catch (err) {
      toast.error("خطا در جستجوی کاربران");
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  }, [axiosAdmin]);

  const handleSearchChange = (e) => {
    const rawValue = e.target.value;
    const converted = toEnglishDigits(rawValue);
    setSearchQuery(converted);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => searchUsers(converted), 800);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setPhone(user.phone);
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setShowDropdown(false);
    setSearchQuery("");
  };

  const lookupPhone = useCallback(async (number) => {
    if (lookupInProgress.current) return;
    if (!number || number.length < 10) return;
    if (selectedUserRef.current?.phone === number) return;

    lookupInProgress.current = true;
    const searchNumber = number;
    try {
      const res = await axiosAdmin.get("/api/admin/users", {
        params: { search: searchNumber, limit: 1 },
      });
      const users = res.data.users || [];

      if (phoneRef.current !== searchNumber) return;

      if (users.length === 1 && users[0].phone === searchNumber) {
        const user = users[0];
        setSelectedUser(user);
        setFirstName(user.firstName || "");
        setLastName(user.lastName || "");
        toast.success("کاربر پیدا شد و مشخصات پر شد");
      }
    } catch (err) {
      // ignore
    } finally {
      lookupInProgress.current = false;
    }
  }, [axiosAdmin]);

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    const numeric = toEnglishDigits(raw).replace(/\D/g, "");
    setPhone(numeric);

    if (selectedUser && numeric !== selectedUser.phone) {
      setSelectedUser(null);
    }

    if (phoneTimer.current) clearTimeout(phoneTimer.current);

    if (numeric.length >= 10) {
      phoneTimer.current = setTimeout(() => {
        lookupPhone(phoneRef.current);
      }, 1500);
    }
  };

  const handlePhoneBlur = () => {
    if (phoneTimer.current) {
      clearTimeout(phoneTimer.current);
      phoneTimer.current = null;
    }
    lookupPhone(phoneRef.current);
  };

  const clearSelectedUser = () => {
    setSelectedUser(null);
    setPhone("");
    setFirstName("");
    setLastName("");
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const isValid = () => {
    const phoneRegex = /^09\d{9}$/;
    if (!phoneRegex.test(phone)) {
      toast.warning("شماره موبایل معتبر نیست");
      return false;
    }
    if (!firstName.trim()) {
      toast.warning("نام مشتری را وارد کنید");
      return false;
    }
    if (!lastName.trim()) {
      toast.warning("نام خانوادگی مشتری را وارد کنید");
      return false;
    }
    if (!paymentType) {
      toast.warning("نوع پرداخت را انتخاب کنید");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!isValid()) return;
    onSubmit({
      user: { phone: phone.trim(), firstName: firstName.trim(), lastName: lastName.trim() },
      paymentType,
    });
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value.replace(/[^\u0600-\u06FF\s]/g, ""));
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value.replace(/[^\u0600-\u06FF\s]/g, ""));
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-5 shadow-lg shadow-purple-500/25">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">مشخصات مشتری و پرداخت</h3>
            <p className="text-purple-100 text-sm mt-0.5">لطفاً مشتری را انتخاب یا مشخصات را وارد کنید</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
        <label className="block text-sm font-bold text-slate-700 mb-1">جستجوی کاربر</label>
        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-slate-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="نام، نام خانوادگی یا شماره تماس..."
            className="w-full pr-10 pl-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 transition-all text-sm"
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
          />
          {isSearching && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
            </div>
          )}
        </div>

        {showDropdown && searchResults.length > 0 && (
          <div className="mt-1 border border-slate-200 rounded-xl bg-white shadow-lg max-h-[400px] overflow-y-auto z-10">
            {searchResults.map((user) => (
              <button
                key={user._id}
                type="button"
                onClick={() => handleSelectUser(user)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-50 transition-colors border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-800">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {user.phone}
                    </p>
                  </div>
                </div>
                <Check className="w-5 h-5 text-purple-500" />
              </button>
            ))}
          </div>
        )}

        {showDropdown && searchResults.length === 0 && searchQuery.trim() && !isSearching && (
          <div className="text-center py-4 text-sm text-slate-500">نتیجه‌ای یافت نشد</div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-slate-700">مشخصات مشتری</h4>
          {selectedUser && (
            <button onClick={clearSelectedUser} className="text-xs text-red-500 hover:underline">
              حذف انتخاب
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">شماره تماس *</label>
            <input
              type="tel"
              maxLength={11}
              minLength={11}
              value={phone}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              placeholder="09xxxxxxxxx"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 transition-all text-sm"
              dir="ltr"
              inputMode="numeric"
            />
          </div>
          <div className="hidden sm:block" />
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">نام *</label>
            <input
              type="text"
              value={firstName}
              onChange={handleFirstNameChange}
              placeholder="نام"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 transition-all text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">نام خانوادگی *</label>
            <input
              type="text"
              value={lastName}
              onChange={handleLastNameChange}
              placeholder="نام خانوادگی"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-400 transition-all text-sm"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-4">
        <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-purple-600" />
          نوع پرداخت
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {["کارت به کارت", "دستگاه POS", "زرین پال", "نامعلوم"].map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setPaymentType(type)}
              className={`px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                paymentType === type
                  ? "border-purple-600 bg-purple-50 text-purple-700 shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-purple-200"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
        >
          <ChevronDown className="w-4 h-4 -rotate-90" />
          بازگشت
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>ثبت قرار</span>
          <Check className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CustomerPaymentStep;